import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Eye, EyeOff, ArrowRight, ChevronLeft } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { Alert, Spinner } from '../components/shared';
import { publicAPI } from '../lib/api';
import toast from 'react-hot-toast';

const roles = [
  {
    id: 'student',
    label: 'Student',
    desc: 'Apply for research topics and scholarships',
    color: '#3b82f6',
    icon: '🎓'
  },
  {
    id: 'lecturer',
    label: 'Lecturer',
    desc: 'Submit research domains for students',
    color: 'var(--teal)',
    icon: '🔬'
  },
  {
    id: 'alumni',
    label: 'Alumni Sponsor',
    desc: 'Fund and mentor outstanding projects',
    color: 'var(--gold)',
    icon: '🏆'
  },
];

function useProgramName() {
  const [name, setName] = useState('Innovation Awards');
  const [season, setSeason] = useState('');
  useEffect(() => {
    publicAPI.getConfig()
      .then(r => {
        if (r.data.program_name) setName(r.data.program_name);
        if (r.data.program_season) setSeason(r.data.program_season);
      })
      .catch(() => {});
  }, []);
  return { name, season };
}

// ── LOGIN ──────────────────────────────────────────────────────────────────
export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const { name: programName, season: programSeason } = useProgramName();
  const [form, setForm] = useState({ email: '', password: '' });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const user = await login(form.email, form.password);
      toast.success(`Welcome back, ${user.first_name}!`);
      navigate(location.state?.from || '/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--ink)',
      backgroundImage: 'radial-gradient(ellipse 80% 60% at 30% 50%, rgba(45,212,191,0.05) 0%, transparent 60%), radial-gradient(ellipse 60% 80% at 70% 50%, rgba(201,162,39,0.04) 0%, transparent 60%)',
      padding: '24px'
    }}>
      <div style={{ width: '100%', maxWidth: 440 }}>
        {/* Brand */}
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: '2rem', fontWeight: 600, color: 'var(--gold)', letterSpacing: '0.05em' }}>
            {programName.toUpperCase()}
          </div>
          {programSeason && (
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.65rem', letterSpacing: '0.18em', color: 'var(--teal)', textTransform: 'uppercase', marginTop: 4 }}>
              {programSeason}
            </div>
          )}
        </div>

        <div className="card" style={{ padding: 36 }}>
          <div style={{ marginBottom: 28 }}>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '1.75rem', fontWeight: 400 }}>Welcome back</h1>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginTop: 6 }}>
              Sign in to your account
            </p>
          </div>

          {searchParams.get('expired') && (
            <div style={{ marginBottom: 20 }}>
              <Alert type="warning">Your session expired. Please sign in again.</Alert>
            </div>
          )}

          {error && (
            <div style={{ marginBottom: 20 }}>
              <Alert type="error">{error}</Alert>
            </div>
          )}

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            <div className="form-group">
              <label className="form-label">Email Address</label>
              <input
                className="form-input"
                type="email"
                placeholder="you@example.com"
                value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                required
                autoFocus
              />
            </div>

            <div className="form-group">
              <label className="form-label">Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  className="form-input"
                  type={showPass ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={form.password}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  required
                  style={{ paddingRight: 44 }}
                />
                <button
                  type="button"
                  onClick={() => setShowPass(v => !v)}
                  style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}
                >
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

              <div style={{ textAlign: "right", marginBottom: 8 }}>
                <a href="/forgot-password" style={{ fontSize: "0.8rem", color: "var(--teal)", textDecoration: "none" }}>Forgot password?</a>
              </div>
            <button
              type="submit"
              className="btn btn-primary btn-lg"
              disabled={loading}
              style={{ marginTop: 8, width: '100%' }}
            >
              {loading ? <Spinner size={18} /> : (
                <>Sign In <ArrowRight size={16} /></>
              )}
            </button>
          </form>

          <div style={{ textAlign: 'center', marginTop: 24, paddingTop: 24, borderTop: '1px solid rgba(255,255,255,0.07)' }}>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
              Don't have an account?{' '}
              <Link to="/register" style={{ color: 'var(--gold)', fontWeight: 500 }}>Register here</Link>
            </p>
          </div>
        </div>

        <p style={{ textAlign: 'center', marginTop: 24, fontSize: '0.78rem', color: 'var(--text-muted)' }}>
          <Link to="/" style={{ color: 'var(--text-muted)' }}>← Back to homepage</Link>
        </p>
      </div>
    </div>
  );
}

// ── REGISTER ───────────────────────────────────────────────────────────────
export function RegisterPage() {
  const navigate = useNavigate();
  const { register } = useAuth();

  const validateMatric = (matric) => {
    if (!matric) return true; // optional at registration
    return /^[A-Za-z]{3}\/\d{2}[A-Za-z]?\/\d{3,6}$/i.test(matric) ||
           /^\d{6,9}$/i.test(matric) ||
           matric.length >= 6;
  };

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };
  const { name: programName, season: programSeason } = useProgramName();
  const [step, setStep] = useState(1);
  const [selectedRole, setSelectedRole] = useState('');
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', password: '', phone: ''
  });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await register({ ...form, role: selectedRole });
      toast.success(selectedRole === 'alumni' ? 'Registration successful! Welcome to Futurum.' : 'Registration successful! Your account is pending approval.');
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed.');
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--ink)',
      backgroundImage: 'radial-gradient(ellipse 80% 60% at 70% 50%, rgba(201,162,39,0.04) 0%, transparent 60%)',
      padding: '24px'
    }}>
      <div style={{ width: '100%', maxWidth: step === 1 ? 600 : 440 }}>
        {/* Brand */}
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: '2rem', fontWeight: 600, color: 'var(--gold)', letterSpacing: '0.05em' }}>
            {programName.toUpperCase()}
          </div>
          {programSeason && (
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.65rem', letterSpacing: '0.18em', color: 'var(--teal)', textTransform: 'uppercase', marginTop: 4 }}>
              {programSeason}
            </div>
          )}
        </div>

        {/* Step 1: Role selection */}
        {step === 1 && (
          <div className="animate-in">
            <div style={{ textAlign: 'center', marginBottom: 32 }}>
              <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '1.75rem', fontWeight: 400 }}>Join the Platform</h1>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginTop: 8 }}>
                Select your role to get started
              </p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 16 }}>
              {roles.map(role => (
                <button
                  key={role.id}
                  onClick={() => { setSelectedRole(role.id); setStep(2); }}
                  style={{
                    background: 'var(--ink-mid)',
                    border: `1px solid rgba(255,255,255,0.08)`,
                    borderRadius: 'var(--radius-lg)',
                    padding: '28px 20px',
                    cursor: 'pointer',
                    textAlign: 'center',
                    transition: 'all 0.25s',
                    color: 'var(--text-primary)',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = role.color; e.currentTarget.style.background = `${role.color}10`; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.background = 'var(--ink-mid)'; }}
                >
                  <span style={{ fontSize: '2rem' }}>{role.icon}</span>
                  <strong style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', color: role.color }}>
                    {role.label}
                  </strong>
                  <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>{role.desc}</p>
                  <span style={{ color: role.color, display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.8rem', marginTop: 4 }}>
                    Select <ArrowRight size={14} />
                  </span>
                </button>
              ))}
            </div>

            <p style={{ textAlign: 'center', marginTop: 28, fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
              Already have an account?{' '}
              <Link to="/login" style={{ color: 'var(--gold)', fontWeight: 500 }}>Sign in</Link>
            </p>
          </div>
        )}

        {/* Step 2: Details */}
        {step === 2 && (
          <div className="animate-in">
            <div className="card" style={{ padding: 36 }}>
              <button
                onClick={() => setStep(1)}
                className="btn btn-ghost btn-sm"
                style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 6 }}
              >
                <ChevronLeft size={16} /> Back
              </button>

              <div style={{ marginBottom: 28 }}>
                <span className="eyebrow">Registering as</span>
                <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '1.75rem', fontWeight: 400, marginTop: 6 }}>
                  {roles.find(r => r.id === selectedRole)?.label}
                </h1>
              </div>

              {error && <div style={{ marginBottom: 20 }}><Alert type="error">{error}</Alert></div>}

              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div className="form-group">
                    <label className="form-label">First Name *</label>
                    <input className="form-input" type="text" placeholder="First name" value={form.first_name}
                      onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Last Name *</label>
                    <input className="form-input" type="text" placeholder="Last name" value={form.last_name}
                      onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} required />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Email Address *</label>
                  <input className="form-input" type="email" placeholder="you@example.com" value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required />
                </div>

                <div className="form-group">
                  <label className="form-label">Phone Number</label>
                  <input className="form-input" type="tel" placeholder="+x xxx xxx xxxx" value={form.phone}
                    onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
                </div>

                <div className="form-group">
                  <label className="form-label">Password *</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      className="form-input"
                      type={showPass ? 'text' : 'password'}
                      placeholder="Min. 8 characters"
                      value={form.password}
                      onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                      required minLength={8}
                      style={{ paddingRight: 44 }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPass(v => !v)}
                      style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}
                    >
                      {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                {selectedRole !== 'alumni' && (
                <div style={{ padding: '14px 16px', background: 'rgba(45,212,191,0.06)', border: '1px solid rgba(45,212,191,0.15)', borderRadius: 'var(--radius-md)', fontSize: '0.8rem', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                  ℹ️ After registration, your account will be reviewed by an administrator before you can access all features.
                </div>
                )}

                <button
                  type="submit"
                  className="btn btn-primary btn-lg"
                  disabled={loading}
                  style={{ marginTop: 8, width: '100%' }}
                >
                  {loading ? <Spinner size={18} /> : (
                    <>Create Account <ArrowRight size={16} /></>
                  )}
                </button>
              </form>
            </div>
          </div>
        )}

        <p style={{ textAlign: 'center', marginTop: 24, fontSize: '0.78rem', color: 'var(--text-muted)' }}>
          <Link to="/" style={{ color: 'var(--text-muted)' }}>← Back to homepage</Link>
        </p>
      </div>
    </div>
  );
}
